<script setup>
import Right from 'assets/icons/right.svg'

// Define some refs
const isOpen = ref(false)
const height = ref('0')
const wrapper = ref(null)

// Set open class
const toggleItemClass = computed(() => isOpen.value ? 'toggle-item-open' : '')

/**
 * Sets the height value based on the isOpen value.
 *
 */
const setHeight = () => {
    height.value = isOpen.value ? `${wrapper.value.scrollHeight}px` : '0'
}

/**
 * Toggles the item by inverting the isOpen value and setting the height.
 *
 */
const toggleItem = () => {
    isOpen.value = !isOpen.value
    setHeight()
}

// Change height item on resize
if (import.meta.client) {
    let timeout = false

    window.addEventListener('resize', () => {
        clearTimeout(timeout)
        timeout = setTimeout(setHeight, 250)
    })
}

const randomUid = useId()
</script>

<template>
    <div class="toggle-item" :class="toggleItemClass">
        <div
            :id="`accordion-${randomUid}`"
            class="toggle-item-header"
            role="button"
            tabindex="0"
            :aria-expanded="isOpen"
            :aria-controls="'content-' + randomUid"
            @click="toggleItem"
            @keydown.space.prevent="toggleItem"
            @keydown.enter.prevent="toggleItem"
        >
            <Right />
            <slot name="title" />
        </div>
        <div
            :id="'content-' + randomUid"
            ref="wrapper"
            class="toggle-item-content-wrapper"
            role="region"
            :aria-labelledby="`accordion-${randomUid}`"
            :aria-hidden="!isOpen"
        >
            <div class="toggle-item-content">
                <slot name="content" :is-open="isOpen" />
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.toggle-item {
    transition: background-color 600ms ease-in-out;
    margin-block-end: px2rem(-40);
}

.toggle-item-header {
    cursor: pointer;
    position: relative;
    padding-inline-start: 30px;

    @include bp(medium1) {
        padding-inline-start: 35px;
    }

    svg {
        height: 20px;
        transition: transform 600ms ease-in-out;
        transform: rotate(-270deg);
        position: absolute;
        top: 4px;
        left: 2px;
        fill: $body-text;

        @include bp(medium1) {
            height: 30px;
            top: 7px;
            left: 2px;
        }

        .toggle-item-open & {
            transform: rotate(-90deg);
        }
    }
}

.toggle-item-content-wrapper {
    transition: height 600ms ease-in-out;
    overflow: hidden;
    height: v-bind('height');
}
</style>
